import React from "react";
import {
  normalizeMediaUrl,
  productName,
  resolveProductDisplayImage,
} from "./productService";
import {
  findAvailableVariant,
  getProductStockQuantity,
  isProductInStock,
  parseStockQuantity,
  shouldSkipStockCheck,
} from "./stockService";
import {
  formatPrice,
  getColorLabel,
  getSizeLabel,
  productUnitPrice,
  uniqueByLabel,
} from "./shopUtils";

function resolveColorCardImage(color, product) {
  if (!color || typeof color !== "object") return "";
  const hint = product?.picture || product?.imgURL;
  return (
    normalizeMediaUrl(color.productImageUrl, hint) ||
    normalizeMediaUrl(color.product_image_url, hint) ||
    normalizeMediaUrl(color.picture, hint) ||
    normalizeMediaUrl(color.image, hint) ||
    normalizeMediaUrl(color.imgURL, hint) ||
    normalizeMediaUrl(color.imageUrl, hint) ||
    ""
  );
}

/** Stickers / prints keep opaque art — no multiply blend on the card image. */
function shouldSkipCardImageBlend(product) {
  const name = productName(product).toLowerCase();
  const sku = String(product?.sku_code || "")
    .trim()
    .toLowerCase();
  const haystack = `${name} ${sku}`;
  if (haystack.includes("sticker")) return true;
  if (
    haystack.includes("photo paper") ||
    haystack.includes("photo print") ||
    haystack.includes("photopaper") ||
    sku === "ppp" ||
    sku === "pp"
  ) {
    return true;
  }
  if (/\bposter\b/.test(haystack) || sku === "ps") return true;
  return false;
}

function ProductCard({ product, onSelect }) {
  const name = productName(product);
  const skipBlend = shouldSkipCardImageBlend(product);
  const isPack =
    product?.pack === true || String(product?.type || "").toLowerCase() === "pack";
  const sizes = uniqueByLabel(
    Array.isArray(product?.product_sizes) ? product.product_sizes : [],
    getSizeLabel
  );
  const sizeLabels = sizes
    .map(getSizeLabel)
    .filter((label) => String(label).trim());
  const colors = uniqueByLabel(
    Array.isArray(product?.color) ? product.color : [],
    getColorLabel
  ).filter((item) => getColorLabel(item));

  const packStock = isPack
    ? parseStockQuantity(product?.quantity) ??
      parseStockQuantity(product?.stock) ??
      parseStockQuantity(product?.available_quantity)
    : null;
  const skipStock = shouldSkipStockCheck(product) || (isPack && packStock == null);
  // Sold out only when every color/size combo is unavailable.
  const availableVariant = skipStock || isPack ? null : findAvailableVariant(product);
  const inStock = skipStock
    ? true
    : isPack
      ? (packStock ?? 0) > 0
      : availableVariant != null || isProductInStock(product);
  const size = availableVariant?.size || product.product_sizes?.[0];
  const availableColorImage = resolveColorCardImage(
    availableVariant?.color,
    product
  );
  // Prefer in-stock color mockup; else generate/catalog image.
  const displayImage =
    availableColorImage ||
    resolveProductDisplayImage(product) ||
    product?.picture ||
    product?.image ||
    product?.imgURL ||
    "";
  const unitPrice = isPack
    ? Number(product?.pack_price ?? product?.price ?? 0) || 0
    : productUnitPrice(product, size);
  const totalStock = skipStock
    ? null
    : isPack
      ? packStock
      : getProductStockQuantity(product);
  const statusStock =
    totalStock != null && Number.isFinite(totalStock) ? totalStock : null;

  const colorCount = colors.length;
  const sizeCount = sizeLabels.length;
  const variantParts = [];
  if (colorCount > 0) {
    variantParts.push(`${colorCount} color${colorCount === 1 ? "" : "s"}`);
  }
  if (sizeCount > 0) {
    variantParts.push(`${sizeCount} size${sizeCount === 1 ? "" : "s"}`);
  }
  const variantsLabel = variantParts.join(" · ");

  return (
    <article className={`shopCard${!inStock ? " is-soldOut" : ""}`}>
      <button
        type="button"
        className="shopCard__hit"
        onClick={() => {
          if (!inStock) return;
          onSelect?.(product);
        }}
        disabled={!inStock}
        aria-disabled={!inStock}
      >
        <div className="shopCard__media">
          {displayImage ? (
            <img
              className={`shopCard__image${skipBlend ? " shopCard__image--noBlend" : " shopCard__image--blend"}`}
              src={displayImage}
              alt={name}
              loading="lazy"
              decoding="async"
            />
          ) : null}
          {!inStock ? (
            <span className="shopCard__soldOutBadge">Sold out</span>
          ) : null}
        </div>
        <div className="shopCard__info">
          <h3 className="shopCard__name">{name}</h3>
          <p className="shopCard__price">{formatPrice(unitPrice)}</p>
          {variantsLabel ? (
            <p className="shopCard__variants">{variantsLabel}</p>
          ) : null}
          {!skipStock ? (
            <p
              className={`shopCard__stock${
                inStock ? " shopCard__stock--ok" : " shopCard__stock--sold"
              }`}
            >
              {inStock
                ? statusStock != null && statusStock < Number.MAX_SAFE_INTEGER
                  ? `In stock: ${statusStock} left`
                  : "In stock"
                : "Sold out"}
            </p>
          ) : null}
        </div>
      </button>
    </article>
  );
}

export default ProductCard;
